import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Input, Radio, RadioGroup, TmdxTextarea } from '@ds';
import { FormSection } from '@features/components/CaseFormBlocks/components';
import { DonorFormRow } from './DonorFormRow';
import { CtScanRow } from './CtScanRow';
import { MatchIdRow } from './MatchIdRow';
import { ReasonForUnallocatedRow } from './ReasonForUnallocatedRow';
import { DonorProfileFormValues } from '../types';
import { createOrganField, OrganAdditionalInfoSectionProps } from '../organField';

const UnitLabel = ({ unit }: { unit: string }) => (
  <span className="text-base-30 text-m-r pointer-events-none select-none">{unit}</span>
);

export const LiverAdditionalInfoSection = ({
  namePrefix,
  title = 'Additional Information',
  showReasonForUnallocated = true,
  showMatchId = false,
}: OrganAdditionalInfoSectionProps) => {
  const { control } = useFormContext<DonorProfileFormValues>();
  const organField = createOrganField(namePrefix);
  const localSurgeonToProcure = useWatch({ control, name: organField('localSurgeonToProcure') });

  return (
    <FormSection title={title}>
      {showMatchId && <MatchIdRow namePrefix={namePrefix} />}
      {showReasonForUnallocated && <ReasonForUnallocatedRow />}

      <DonorFormRow title="T.Bili – Result 1">
        <div className="w-[332px]">
          <Controller
            name={organField('tbiliResult1')}
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                type="number"
                min="0"
                step="any"
                aria-label="T.Bili Result 1"
                endContent={<UnitLabel unit="mg/dL" />}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      </DonorFormRow>

      <DonorFormRow title="T.Bili – Result 2">
        <div className="w-[332px]">
          <Controller
            name={organField('tbiliResult2')}
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                type="number"
                min="0"
                step="any"
                aria-label="T.Bili Result 2"
                endContent={<UnitLabel unit="mg/dL" />}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      </DonorFormRow>

      <DonorFormRow title="SGOT / AST – Result 1">
        <div className="w-[332px]">
          <Controller
            name={organField('sgotAstResult1')}
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                type="number"
                min="0"
                step="any"
                aria-label="SGOT/AST Result 1"
                endContent={<UnitLabel unit="U/L" />}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      </DonorFormRow>

      <DonorFormRow title="SGOT / AST – Result 2">
        <div className="w-[332px]">
          <Controller
            name={organField('sgotAstResult2')}
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                type="number"
                min="0"
                step="any"
                aria-label="SGOT/AST Result 2"
                endContent={<UnitLabel unit="U/L" />}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      </DonorFormRow>

      <DonorFormRow title="SGPT / ALT – Result 1">
        <div className="w-[332px]">
          <Controller
            name={organField('sgptAltResult1')}
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                type="number"
                min="0"
                step="any"
                aria-label="SGPT/ALT Result 1"
                endContent={<UnitLabel unit="U/L" />}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      </DonorFormRow>

      <DonorFormRow title="SGPT / ALT – Result 2">
        <div className="w-[332px]">
          <Controller
            name={organField('sgptAltResult2')}
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                type="number"
                min="0"
                step="any"
                aria-label="SGPT/ALT Result 2"
                endContent={<UnitLabel unit="U/L" />}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      </DonorFormRow>

      <CtScanRow namePrefix={namePrefix} />

      <DonorFormRow title="Local Surgeon to Procure?">
        <Controller
          name={organField('localSurgeonToProcure')}
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <RadioGroup
              orientation="horizontal"
              value={value === null || value === undefined ? '' : String(value)}
              isInvalid={invalid}
              errorMessage={error?.message}
              onValueChange={(v) => onChange(v === 'true')}
              {...field}
            >
              <Radio className="w-[100px] max-w-none" classNames={{ label: 'pl-1' }} value="true" size="lg">
                Yes
              </Radio>
              <Radio className="w-[100px] max-w-none" classNames={{ label: 'pl-1' }} value="false" size="lg">
                No
              </Radio>
            </RadioGroup>
          )}
        />
      </DonorFormRow>

      {localSurgeonToProcure && (
        <DonorFormRow title="Local Surgeon to Procure Details" multiRow>
          <Controller
            name={organField('localSurgeonToProcureDetails')}
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <TmdxTextarea
                aria-label="Local surgeon to procure details"
                maxLength={512}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </DonorFormRow>
      )}

      <DonorFormRow title="Sedation" multiRow>
        <Controller
          name={organField('sedation')}
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <Input
              placeholder="Enter sedation details"
              aria-label="Sedation"
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
      </DonorFormRow>
    </FormSection>
  );
};
